import React, { useState } from 'react';
import api from '../services/ApiService';
import AnimatedLogo from './AnimatedLogo';
import { useBranding } from '../context/BrandingContext';

const REMEMBER_KEY = 'ce_remember';
const readRemember = () => {
  try { const r = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null'); return r && r.u ? { u: r.u, p: r.p ? decodeURIComponent(escape(atob(r.p))) : '' } : null; } catch (_) { return null; }
};
const writeRemember = (on, u, p) => {
  try {
    if (on) localStorage.setItem(REMEMBER_KEY, JSON.stringify({ on: true, u, p: btoa(unescape(encodeURIComponent(p))) }));
    else localStorage.setItem(REMEMBER_KEY, JSON.stringify({ on: false }));
  } catch (_) {}
};

export default function Login({ onLogin }) {
  // 2026-10-30: "Remember username and password" — same saved login as the phone app (/v2, same site,
  // localStorage ce_remember), so signing in once on this computer fills both.
  const _saved = readRemember();
  const [username, setUsername]             = useState(_saved?.u || '');
  const [portalPassword, setPortalPassword] = useState(_saved?.p || '');
  const [remember, setRemember]             = useState(() => { try { const r = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null'); return !r || r.on !== false; } catch (_) { return true; } });
  const [error, setError]                   = useState('');
  const [loading, setLoading]               = useState(false);
  const [totp, setTotp]                     = useState('');
  const [twofa, setTwofa]                   = useState('');  // '' | 'enroll' | 'verify'
  const [qr, setQr]                         = useState('');
  const [trustDevice, setTrustDevice]        = useState(false);

  const branding = useBranding();

  // Derive whether tenant has custom branding configured
  const hasLogo = !!branding.logo;
  const hasCustomBrand = hasLogo || !!branding.primaryColor ||
    (!!branding.companyName && branding.companyName !== 'CallEssential');
  // "Powered by SynergyTalk" attribution is shown only for the platform default
  // experience, or when the tenant has not opted into white-label removal.
  const showPoweredBy = !hasCustomBrand && !branding.hidePoweredBy;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const tenantSubdomain = new URLSearchParams(window.location.search).get('tenant') || undefined;
      const data = await api.login(username.trim(), portalPassword, tenantSubdomain, totp.trim() || undefined, trustDevice);
      if (data && (data.token || data.twofa)) writeRemember(remember, username.trim(), portalPassword);
      // Admin 2FA: backend returns { twofa: 'enroll'|'verify', qr?, error? } (HTTP 200)
      if (data.twofa) {
        setTwofa(data.twofa);
        if (data.qr) setQr(data.qr);
        setError(data.error || '');
        setLoading(false);
        return;
      }
      const sessionData = {
        token:       data.token,
        user:        data.user,
        sipPassword: data.sipPassword || null,
        sipUsername: data.sipUsername || null,
        dids:        data.dids || []
      };
      localStorage.setItem('token', data.token);
      localStorage.setItem('ce_session', JSON.stringify(sessionData));
      try {
        const _flags = "max-age=86400; SameSite=Lax; Secure";
        document.cookie = "ce_token=" + encodeURIComponent(data.token) + "; " + _flags;
        document.cookie = "ce_sess=" + btoa(encodeURIComponent(JSON.stringify(sessionData))) + "; " + _flags;
      } catch (_) {}
      onLogin(sessionData);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Page background: tenant login_bg image > tenant secondary color > platform default gradient
  const hasPhotoBg = !!branding.loginBackground;
  const pageStyle = hasPhotoBg
    ? {
        backgroundImage: `url(${branding.loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : branding.secondaryColor
    ? { background: branding.secondaryColor }
    : {};

  // Card style: frosted WHITE glass over any background (login always uses light card)
  const cardOverride = hasPhotoBg ? {
    background: 'rgba(255,255,255,0.90)',
    border: '1px solid rgba(255,255,255,0.7)',
    boxShadow: '0 24px 64px rgba(0,0,0,0.30), 0 0 0 1px rgba(255,255,255,0.3)',
    backdropFilter: 'blur(22px) saturate(1.5)',
    WebkitBackdropFilter: 'blur(22px) saturate(1.5)',
  } : {};

  // Text colors — dark on frosted white card, light on default dark card
  const textPrimary  = hasPhotoBg ? '#0d1b38' : '#f9fafb';
  const textMuted    = hasPhotoBg ? '#374151' : '#9ca3af';
  const textSubtle   = hasPhotoBg ? '#6b7280'  : 'rgba(156,163,175,0.7)';

  // Accent color: tenant primary color used directly for button
  const accentColor  = branding.primaryColor || '#a855f7';
  const accentRgba   = hasPhotoBg
    ? `${accentColor}22`
    : (branding.primaryColor ? `${branding.primaryColor}33` : 'rgba(168,85,247,0.2)');

  return (
    <div className="login-page" style={pageStyle}>
      <form className="login-card" onSubmit={submit} autoComplete="on" style={cardOverride}>

        {/* Logo + brand name */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {hasLogo ? (
            /* Tenant logo alone — wordmark logos already carry the name. */
            <img
              src={branding.logo}
              alt={branding.companyName}
              style={{ maxWidth: '220px', maxHeight: '150px', objectFit: 'contain', display: 'inline-block' }}
            />
          ) : hasCustomBrand ? (
            /* Branded tenant without a logo — name as text wordmark. */
            <div style={{ fontWeight: 800, fontSize: '22px', color: textPrimary, letterSpacing: '-0.4px' }}>
              {branding.companyName}
            </div>
          ) : (
            /* Platform default. */
            <>
              <AnimatedLogo width={154} style={{ display: 'inline-block' }} />
              <div style={{ marginTop: '12px', fontWeight: 800, fontSize: '18px', color: textPrimary, letterSpacing: '-0.3px' }}>
                CallEssential
              </div>
              {showPoweredBy && (
                <div style={{ fontSize: '12px', color: '#c084fc', fontWeight: 500, marginTop: '2px', letterSpacing: '0.4px' }}>
                  by Synergytalk
                </div>
              )}
            </>
          )}
        </div>

        {/* Heading */}
        <h1 style={{ fontSize: '15px', marginBottom: '2px', fontWeight: 600, color: textMuted }}>
          Sign in to your account
        </h1>
        <p style={{ display: 'none' }}>placeholder</p>

        {/* Divider */}
        <div style={{
          height: '1px',
          background: accentRgba,
          margin: '6px 0 16px'
        }} />

        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
          name="username"
          autoComplete="username"
          required
          style={hasPhotoBg ? { background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(0,46,93,0.2)', color: '#0d1b38' } : {}}
        />

        <input
          type="password"
          value={portalPassword}
          onChange={(e) => setPortalPassword(e.target.value)}
          placeholder="Password"
          name="password"
          autoComplete="current-password"
          required
          style={hasPhotoBg ? { background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(0,46,93,0.2)', color: '#0d1b38' } : {}}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: textMuted, margin: '2px 0 4px', cursor: 'pointer' }}>
          <input type="checkbox" checked={remember} onChange={(e) => { setRemember(e.target.checked); if (!e.target.checked) writeRemember(false); }} style={{ width: 'auto', margin: 0 }} />
          Remember username and password
        </label>

        {twofa && (
          <div style={{ marginTop: 4 }}>
            {twofa === 'enroll' && (
              <>
                <p style={{ fontSize: '12px', color: textMuted, margin: '4px 0 8px' }}>
                  Two-factor authentication is required for your account. Scan this QR code with Google Authenticator, Authy, or 1Password, then enter the 6-digit code.
                </p>
                {qr && (
                  <img src={qr} alt="2FA QR code" style={{ width: 160, height: 160, display: 'block', margin: '0 auto 10px', background: '#fff', borderRadius: 8, padding: 6 }} />
                )}
              </>
            )}
            {twofa === 'verify' && (
              <p style={{ fontSize: '12px', color: textMuted, margin: '4px 0 8px' }}>
                Enter the 6-digit code from your authenticator app.
              </p>
            )}
            <input
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              style={hasPhotoBg ? { background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(0,46,93,0.2)', color: '#0d1b38' } : {}}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: textMuted, marginTop: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
              Trust this device for 30 days (skip the code on this computer)
            </label>
          </div>
        )}

        {error && <div className="error">{error}</div>}

        <button
          type="submit"
          disabled={loading}
          style={{
            background: accentColor !== '#a855f7'
              ? accentColor
              : 'linear-gradient(135deg,#7e22ce,#a855f7)',
            boxShadow: `0 4px 20px ${accentColor}44`,
            color: 'white',
          }}
        >
          {loading ? 'Signing in…' : (twofa ? 'Verify' : 'Sign In')}
        </button>

        {/* Footer */}
        <p style={{
          marginTop: '20px',
          marginBottom: 0,
          fontSize: '11px',
          color: textSubtle,
          textAlign: 'center',
        }}>
          {showPoweredBy ? 'Secure TLS · WebRTC · Powered by SynergyTalk' : 'Secure TLS · WebRTC'}
        </p>
      </form>
    </div>
  );
}
